import { Buffer } from "buffer";
import type { AccountInfo, Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { VIRTUAL_SOL_LAMPORTS } from "./constants";
import { totalFeeBps } from "./curve";
import { discoverPoolRegistry } from "./discovery";
import { loadTokenMetadata } from "./metadata";
import { readMintBase, readU64, TOKEN_2022_PROGRAM_ID } from "./token2022";
import type { Market, PoolRegistryEntry } from "./types";

async function hydrateMarket(
  entry: PoolRegistryEntry,
  accounts: readonly (AccountInfo<Buffer> | null)[],
): Promise<Market | undefined> {
  const [poolAccount, solVault, tokenVault, mintAccount] = accounts;
  if (!poolAccount || !solVault || !tokenVault || !mintAccount) return undefined;
  if (!mintAccount.owner.equals(TOKEN_2022_PROGRAM_ID)) return undefined;
  const poolData = poolAccount.data;
  const creator = new PublicKey(poolData.subarray(16, 48)).toBase58();
  const mint = new PublicKey(poolData.subarray(48, 80));
  if (mint.toBase58() !== entry.mint) return undefined;

  const tokenReserveRaw = readU64(tokenVault.data, 64);
  const { supplyRaw, decimals } = readMintBase(mintAccount);
  if (!tokenReserveRaw || decimals === undefined) return undefined;
  const realSolLamports = BigInt(solVault.lamports);
  const virtualSolLamports = VIRTUAL_SOL_LAMPORTS + realSolLamports;
  const scale = 10 ** decimals;
  const tokenReserve = Number(tokenReserveRaw) / scale;
  const supply = Number(supplyRaw) / scale;
  const priceSol = Number(virtualSolLamports) / 1e9 / tokenReserve;
  const marketCapSol = priceSol * supply;
  const metadata = await loadTokenMetadata(mint, mintAccount);
  const createdAt = poolData.length >= 136 ? Number(readU64(poolData, 128)) : undefined;

  return {
    ...entry,
    ...metadata,
    creator,
    decimals,
    supplyRaw,
    tokenReserveRaw,
    realSolLamports,
    virtualSolLamports,
    priceSol,
    marketCapSol,
    feeBps: totalFeeBps(marketCapSol),
    createdAt,
  };
}

export async function loadMarkets(connection: Connection): Promise<Market[]> {
  const registry = await discoverPoolRegistry(connection);
  const keys = registry.flatMap((entry) => [
    new PublicKey(entry.pool),
    new PublicKey(entry.solVault),
    new PublicKey(entry.tokenVault),
    new PublicKey(entry.mint),
  ]);
  const accounts: (AccountInfo<Buffer> | null)[] = [];
  // Some browser-friendly public RPCs cap getMultipleAccounts below Solana's
  // protocol limit. Small sequential chunks keep the static frontend portable.
  const chunkSize = connection.rpcEndpoint.includes("publicnode.com") ? 10 : 100;
  for (let offset = 0; offset < keys.length; offset += chunkSize) {
    accounts.push(
      ...(await connection.getMultipleAccountsInfo(
        keys.slice(offset, offset + chunkSize),
        "confirmed",
      )),
    );
  }
  const markets = await Promise.all(
    registry.map((entry, index) =>
      hydrateMarket(entry, accounts.slice(index * 4, index * 4 + 4)),
    ),
  );
  return markets
    .filter((market): market is Market => Boolean(market))
    .sort((a, b) => b.marketCapSol - a.marketCapSol);
}
